import { ref, type Ref } from "vue";
import type { FieldServiceInfo } from "./useFieldService";

/**
 * Shared composable for handling overhead toggle confirmation dialog
 * Used by both RentalTaskDetail and ForceTaskDetail
 */
export function useOverheadConfirmation<T extends { overHead: boolean } & FieldServiceInfo>(
  state: { taskInfo: T },
  setOverhead: (val: boolean) => void,
  additionalReset?: () => void
) {
  const showOverheadConfirm = ref(false);
  const pendingOverheadValue = ref(false);

  /**
   * Handle overhead toggle change - shows confirmation if switching to overhead
   */
  const handleOverheadChange = (value: boolean) => {
    if (value) {
      // Show confirmation dialog when switching to overhead
      pendingOverheadValue.value = value;
      showOverheadConfirm.value = true;
    } else {
      // Directly set to false without confirmation
      setOverhead(false);
    }
  };

  /**
   * Clear all field service fields to default values
   */
  const clearFieldServiceFields = () => {
    state.taskInfo.additionInfo = false;
    state.taskInfo.clientName = "";
    state.taskInfo.location = "";
    state.taskInfo.manufacturer = "";
    state.taskInfo.model = "";
    state.taskInfo.vehRegNo = "";
    state.taskInfo.serialNo = "";
    state.taskInfo.SMUHour = undefined;
    state.taskInfo.travelKMS = undefined;
    state.taskInfo.customerEmail = "";
    state.taskInfo.fieldServiceNote = "";
    state.taskInfo.customerAcceptance = "";
    state.taskInfo.mechanicalAcceptance = "";
    state.taskInfo.isCustomerAcceptance = "";
    state.taskInfo.isMechanicalAcceptance = "";
    state.taskInfo.customerAcceptanceName = "";
  };

  /**
   * Handle overhead confirmation - clears form data and sets overhead
   */
  const handleOverheadConfirm = () => {
    if (pendingOverheadValue.value) {
      // Clear field service fields
      clearFieldServiceFields();
      
      // Call additional reset if provided (for component-specific fields)
      if (additionalReset) {
        additionalReset();
      }
    }
    
    setOverhead(pendingOverheadValue.value);
    showOverheadConfirm.value = false;
  };

  /**
   * Handle overhead cancellation - reverts the switch
   */
  const handleOverheadCancel = () => {
    state.taskInfo.overHead = false;
    showOverheadConfirm.value = false;
  };

  return {
    showOverheadConfirm,
    pendingOverheadValue,
    handleOverheadChange,
    handleOverheadConfirm,
    handleOverheadCancel,
  };
}
